import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, setDoc, doc, getDoc, deleteDoc, limit, orderBy, writeBatch, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createNotification } from '../services/NotificationSystem';
import { buildItemsFromOrders, normalizePriceItems, generateStatementNumber, serializeStatementItems } from '../utils/statementItems';
import { fetchDomesticBank } from '../utils/domesticBank';
import { Plus, FileText, ChevronLeft, ChevronRight, RefreshCw, Trash2, Inbox, Check, HardDriveDownload, FolderSearch, CalendarCheck, FileSpreadsheet, Eye } from 'lucide-react';
import PageHeader from './common/PageHeader';
import CreateTransactionStatement from './CreateTransactionStatement';
import ViewTransactionStatement from './ViewTransactionStatement';
import StatementPreviewModal from './StatementPreviewModal';   // 읽기 전용 미리보기 (2026-10-06)
import ExpectedStatementModal from './ExpectedStatementModal';   // 치과 「기간 예상 기공료」 모달 (2026-10-07)
import { getFeatures, isClinicExpectedStatementOn, getClinicExpectedStatementOpenAtText } from '../utils/features';
import {
    fetchExpectedStatement, describeExpectedStatementError, validateExpectedRange, toYmd,
    EXPSTMT_CARD_CAPTION, EXPSTMT_NOT_SHARED, EXPSTMT_NO_PRICE, EXPSTMT_MAX_DAYS,
} from '../services/expectedStatementApi';
import StatementErrorBoundary from './StatementErrorBoundary';
import ParsedCasesInbox from './ParsedCasesInbox';
import { getStatementDeleteErrorMessage } from '../utils/statementDeleteError';
import { exportTaxMonthlyReport, exportOrderHistory, lastMonthValue, toDateInput as toDateInputX, LAB_STAFF_EXPORT_MSG, toJsDate as toJsDateX } from '../utils/excelExport';
import { statementMonthKeyDate } from '../utils/statementView';
// 거래명세서 1단계 (2026-10-23) — 요약 카드·마감 초안·입금 열·삭제 시 케이스 되돌림 (utils는 dev A 소유, import만)
import SummaryCards, { invalidateSummaryCache } from './statement/SummaryCards';
import { planClosingFor, closingAutoDraft, closingBlockedText, loadStatementsForClosing } from './statement/closingDraft';
import { paymentStateInfo, PAYMENT_STATE_STYLE, isLedgerStatement } from './statement/paymentState';
import { deleteDraftAndRestoreCases } from '../utils/caseRestore';
import { statementPaymentDetail, invalidateReceivables } from '../utils/receivables';
import { partnerKeyOfStatement, isMemberPartnerKey, kstYmd } from '../utils/billingCycle';
import { isLabStaff } from '../utils/excelExport';

// 외주 명세서 종류 — CAD 디자인 외주(design_outsource)·제조 의뢰(outsource_mf, 2026-10-13)
const OUTSOURCE_KINDS = ['design_outsource', 'outsource_mf'];
// 공유 링크(비회원 치과)로 온 수정 요청이 있고 치과가 아직 [확인]을 안 누름 — 상세 화면 배지·스테퍼와 같은 기준 (2026-10-24 재점검 P2-6)
const hasLinkRevision = (s) => s?.status === 'confirmed' && Number(s?.shareRevisionCount) > 0 && !s?.shareConfirmedAt;

// readOnly(prop): 결제하지 않은 치과의 「받은 명세서」 — ClinicSubGate 가 잠금 대신 읽기 전용으로 렌더할 때 true (대표 10-15 "응")
function TransactionStatementList({ user, initialPartner, initialYear, initialMonth, initialView, onBack: onBackToSettlement, readOnly: readOnlyProp = false }) {
    const navigate = useNavigate();
    const [statements, setStatements] = useState([]);
    const [filteredStatements, setFilteredStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    // 불러오기 실패 표시 (2026-10-23): null=정상 / 'full'=목록을 못 불러옴 / 'partial'=일부 쿼리 실패(목록이 빠져 있을 수 있음)
    const [loadError, setLoadError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);   // 삭제 진행 중인 명세서 id (버튼 잠금)
    const [currentView, setCurrentView] = useState(initialView || 'list'); // '/inbox' 사이드메뉴 진입 시 'inbox'
    const [selectedStatementId, setSelectedStatementId] = useState(null);
    // 미리보기 모달 (2026-10-06): 열려 있는 명세서 id. 이전/다음은 현재 필터된 목록의 발행본 순서 (D3 양쪽 노출 · D4)
    const [previewId, setPreviewId] = useState(null);
    const [editDraftId, setEditDraftId] = useState(null); // 임시저장 이어쓰기
    const [correctFromId, setCorrectFromId] = useState(null); // 정산 완료본 [고쳐서 다시 발행] 원본 id (2026-10-23)
    const [createClinicId, setCreateClinicId] = useState(null); // 보드/수집함에서 넘어온 거래처 프리셀렉트
    const [createFrom, setCreateFrom] = useState(''); // 수집함에서 넘어온 기간(시작)
    const [createTo, setCreateTo] = useState('');     // 수집함에서 넘어온 기간(끝)
    const [createAutoDraft, setCreateAutoDraft] = useState(null);   // 자동 초안 지시 (마감 초안·[이번 초안에 넣기], 2026-10-23)
    const [recvByPartner, setRecvByPartner] = useState(null);        // 받을 돈 원장 — '입금' 열용 (대표만)
    const location = useLocation();
    const [summaryRefresh, setSummaryRefresh] = useState(0);         // 카드 다시 계산 신호 (삭제 뒤)
    const addingUnbilledRef = useRef(false);                          // [이번 초안에 넣기] 중복 실행 가드

    // 필터
    const [partners, setPartners] = useState([]);
    // 월 마감 현황 보드 (기공소용)
    const [boardRows, setBoardRows] = useState([]);
    const [boardLoading, setBoardLoading] = useState(false);
    const [inboxPendingCount, setInboxPendingCount] = useState(0); // 에이전트 수집 대기 건수 (수집함 버튼 배지)
    // 온보딩 체크리스트 (기공소 · 명세서 0건) — 에이전트 연결/수집 이력 상태
    const [agentConnected, setAgentConnected] = useState(undefined); // undefined=로딩중, true/false=판정됨
    const [hasParsedCases, setHasParsedCases] = useState(false);     // 수집 이력(상태 무관) 존재 여부
    const [batchCreating, setBatchCreating] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(initialPartner || 'all');
    // 상태 칩 필터 (2026-08-26): 전체 / 작성 중 / 발행됨 / 수정 요청 / 정산 완료 — 클라이언트 필터
    const [statusChip, setStatusChip] = useState('all');
    // 외주 의뢰서 확장(10-13, 계획 §1-2): [전체/치과/외주] — 외주 명세서(CAD 디자인·제조 의뢰)가 하나라도 있을 때만 보인다
    const [partyChip, setPartyChip] = useState('all');
    const [selectedYear, setSelectedYear] = useState(initialYear || new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(initialMonth || new Date().getMonth() + 1);
    // 기간 보기: 'month' = 월별, 'range' = 기간 직접 선택
    const [periodMode, setPeriodMode] = useState('month');
    // 날짜 기준: 'createdAt' 작성일 / 'dueDate' 마감일 (의뢰서=dueDate, 명세서=기간 종료일)
    const [dateBasis, setDateBasis] = useState('createdAt');
    // Timestamp/Date/문자열 혼재 흡수
    const toJsDate = (v) => (v?.toDate?.() ? v.toDate() : (v ? new Date(v) : null));
    const toDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const [rangeStart, setRangeStart] = useState(() => toDateInput(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
    const [rangeEnd, setRangeEnd] = useState(() => toDateInput(new Date()));
    // 엑셀 내려받기 2종 (2026-09-04 대표 지시) — A 세무 자료(월, 기본 지난달) / B 주문내역(기간, 기본 이번 달)
    const [taxMonth, setTaxMonth] = useState(() => lastMonthValue());
    const [exportingTax, setExportingTax] = useState(false);
    const [orderFrom, setOrderFrom] = useState(() => toDateInputX(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
    const [orderTo, setOrderTo] = useState(() => toDateInputX(new Date()));
    const [exportingOrders, setExportingOrders] = useState(false);
    // 화면 정리 UI만 (2026-10-23 §5-1) — 접기/펼치기 전용 상태. 동작·조회 로직은 그대로
    const [downloadOpen, setDownloadOpen] = useState(false);     // [내려받기 ▾] 메뉴 펼침
    const [filterMoreOpen, setFilterMoreOpen] = useState(false); // 필터 '더 보기 ▾' 펼침

    // 💰 치과 「기간으로 예상 기공료 보기」 카드 (2026-10-07 — 기획 §3-3 · 계약 docs/계약_기간예상명세서_웹_2026-10-07.md · 법무 §3 ☆)
    //   플래그 config/features.clinicExpectedStatement(strict) OFF면 카드 비노출·CF 호출 0. 사용자가 [보기]를 눌러야 1회 호출(자동 호출 금지).
    //   Firestore 쓰기 0 · 저장 없음. 서버 응답은 ExpectedStatementModal 에 그대로 넘긴다.
    const [features, setFeatures] = useState(null);
    const [expFrom, setExpFrom] = useState(() => toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
    const [expTo, setExpTo] = useState(() => toYmd(new Date()));
    const [expLabId, setExpLabId] = useState('');
    const [expLoading, setExpLoading] = useState(false);
    const [expNotice, setExpNotice] = useState(null);   // { kind: 'error'|'info'|'feature-off', text, retry?: bool }
    const [expData, setExpData] = useState(null);       // 모달에 넘길 서버 응답(shared:true)
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, [user?.uid]);

    // 현재 선택된 기간 (end는 미포함 경계)
    const getPeriod = () => {
        if (periodMode === 'range' && rangeStart && rangeEnd) {
            const start = new Date(`${rangeStart}T00:00:00`);
            const endInclusive = new Date(`${rangeEnd}T00:00:00`);
            const end = new Date(endInclusive.getFullYear(), endInclusive.getMonth(), endInclusive.getDate() + 1);
            const fmt = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
            return { start, end, label: `${fmt(start)} ~ ${fmt(endInclusive)}` };
        }
        return {
            start: new Date(selectedYear, selectedMonth - 1, 1),
            end: new Date(selectedYear, selectedMonth, 1),
            label: `${selectedYear}년 ${selectedMonth}월`,
        };
    };

    useEffect(() => {
        if (user) fetchPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // 선택 기간이 바뀌면 서버 재조회 — 목록 쿼리에 기간 범위 where가 걸려 있어 월 이동 시 다시 불러온다 (2026-08-26)
    useEffect(() => {
        if (user) fetchStatements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, selectedYear, selectedMonth, periodMode, rangeStart, rangeEnd]);

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statements, selectedPartner, selectedYear, selectedMonth, periodMode, rangeStart, rangeEnd, dateBasis, statusChip, partyChip]);

    const fetchPartners = async () => {
        try {
            const partnersMap = new Map();
            // 내 연결만 조회 (전체 스캔 방지) + 직원 계정은 회사 ID 기준
            const myId = user?.companyId || user?.uid;
            const [sentSnap, recvSnap] = await Promise.all([
                getDocs(query(collection(db, 'connections'), where('requesterId', '==', myId), where('status', '==', 'accepted'))),
                getDocs(query(collection(db, 'connections'), where('receiverId', '==', myId), where('status', '==', 'accepted'))),
            ]);

            const partnerIds = new Set();
            [...sentSnap.docs, ...recvSnap.docs].forEach(docSnap => {
                const connection = docSnap.data();
                const partnerId = connection.requesterId === myId ? connection.receiverId : connection.requesterId;
                if (partnerId && partnerId !== myId) partnerIds.add(partnerId);
            });

            // 상대 정보 병렬 조회 (순차 getDoc N+1 제거)
            await Promise.all(Array.from(partnerIds).map(async (partnerId) => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', partnerId));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        partnersMap.set(partnerId, {
                            id: partnerId,
                            name: userData.businessName || userData.companyName || userData.name || '이름 없음',
                            businessType: userData.businessType || '',   // 예상 기공료 카드의 기공소 필터용 (2026-10-07)
                        });
                    }
                } catch (error) {
                    console.error('파트너 정보 조회 실패:', error);
                }
            }));

            // 이메일 매핑(labSettings.clinicEmailMapping)에서 거래처 추가
            try {
                const uid = user?.companyId || user?.uid;
                const labSettingsDoc = await getDoc(doc(db, 'labSettings', uid));
                if (labSettingsDoc.exists()) {
                    const clinicMapping = labSettingsDoc.data()?.clinicEmailMapping || {};
                    for (const [clinicId, data] of Object.entries(clinicMapping)) {
                        const clinicName = data.clinicName || '';
                        if (!clinicName || partnersMap.has(clinicId)) continue;

                        try {
                            const userDoc2 = await getDoc(doc(db, 'users', clinicId));
                            if (userDoc2.exists()) {
                                partnersMap.set(clinicId, {
                                    id: clinicId,
                                    name: userDoc2.data().businessName || clinicName,
                                    businessType: userDoc2.data().businessType || '',
                                });
                                continue;
                            }
                        } catch (e) { /* ignore */ }

                        const key = `email_${clinicName}`;
                        if (!partnersMap.has(key)) {
                            partnersMap.set(key, { id: key, name: clinicName });
                        }
                    }
                }
            } catch (emailError) {
                console.error('이메일 매핑 거래처 조회 실패:', emailError);
            }

            setPartners(Array.from(partnersMap.values()));
        } catch (error) {
            console.error('거래처 목록 조회 실패:', error);
        }
    };

    // 작성 중(draft) 명세서 삭제 — 발행된 명세서는 상세 화면에서만 삭제 가능
    const handleDeleteDraft = async (e, s) => {
        e.stopPropagation();   // 행 클릭(편집 화면 이동) 방지
        if (deletingId) return;   // 중복 클릭 방지
        // 발행본(confirmed 등)은 목록에서 지울 수 없다 — 무반응 대신 이유를 알리고 상세로 안내 (2026-08-08)
        if (s.status !== 'draft') {
            const go = window.confirm('발행된 거래명세서는 명세서를 열어서 삭제할 수 있습니다.\n\n지금 명세서를 열까요?');
            if (go) {
                setSelectedStatementId(s.id);
                setCurrentView('detail');
            }
            return;
        }
        const partner = s.direction === 'sent' ? s.toUserName : s.fromUserName;
        if (!window.confirm(`작성 중인 명세서를 삭제할까요?\n\n거래처: ${partner || '-'}\n번호: ${s.statementNumber || '-'}\n\n삭제하면 되돌릴 수 없습니다. (원본 의뢰서는 그대로 유지됩니다)`)) return;
        setDeletingId(s.id);
        try {
            // (2026-10-23 1단계) 명세서 삭제 + 묶인 수집 케이스 되돌림(pending)을 writeBatch 하나로 — 케이스가 조용히 사라지지 않게
            const labIdX = s.fromUserId || user?.companyId || user?.uid;
            await deleteDraftAndRestoreCases(s.id, labIdX);
            setStatements(prev => prev.filter(x => x.id !== s.id));
            invalidateReceivables(labIdX);       // 원장 캐시 지움 (P1-4)
            invalidateSummaryCache(labIdX);
            setSummaryRefresh(k => k + 1);       // 카드 ①② 다시 계산 — 되돌린 케이스가 '청구 안 됨'으로 다시 잡힌다
        } catch (err) {
            // 조용한 실패 금지 — 거부 사유를 그대로 안내한다 (2026-08-08)
            console.error('명세서 삭제 실패:', err);
            alert(getStatementDeleteErrorMessage(err, s, user));
        } finally {
            setDeletingId(null);
        }
    };

    const fetchStatements = async () => {
        setLoading(true);
        setLoadError(null);
        let partialFail = false;   // 기간(endDate) 기준 병행 조회 실패 — 목록이 빠져 있을 수 있음
        try {
            const uid = user?.companyId || user?.uid;
            const statementsRef = collection(db, 'transactionStatements');
            // (2026-08-26 성능) 전량 스캔 → 선택 기간 기준 createdAt 범위 조회.
            // 마감일 기준(endDate) 필터·기간 경계 문서 대응으로 ±62일 여유를 둔다.
            // draft는 별도 쿼리로 기간 무관 항상 로드 — 상단 "작성 중" 고정 섹션용 (월 필터에 안 걸림)
            const { start, end } = getPeriod();
            const MARGIN_MS = 62 * 24 * 3600 * 1000;
            const rangeStartD = new Date(start.getTime() - MARGIN_MS);
            const rangeEndD = new Date(end.getTime() + MARGIN_MS);
            const sentQ = query(statementsRef, where('fromUserId', '==', uid),
                where('createdAt', '>=', rangeStartD), where('createdAt', '<=', rangeEndD), orderBy('createdAt', 'desc'));
            const recvQ = query(statementsRef, where('toUserId', '==', uid),
                where('createdAt', '>=', rangeStartD), where('createdAt', '<=', rangeEndD), orderBy('createdAt', 'desc'));
            // (2026-08-31 회귀 수정) 기간(endDate) 기준 병행 조회 — 오래 묵힌 draft를 이어써 발행하면
            // createdAt이 기간보다 몇 달 이르게 남아(이어쓰기 정책상 createdAt 보존) createdAt 범위만으로는
            // 그 달 화면에서 빠진다 (실사례: 우리미소 8월분 TS-20260529-550, createdAt 5/29·기간 ~8/31).
            const sentEndQ = query(statementsRef, where('fromUserId', '==', uid),
                where('endDate', '>=', rangeStartD), where('endDate', '<=', rangeEndD), orderBy('endDate', 'desc'));
            const recvEndQ = query(statementsRef, where('toUserId', '==', uid),
                where('endDate', '>=', rangeStartD), where('endDate', '<=', rangeEndD), orderBy('endDate', 'desc'));
            const draftQ = query(statementsRef, where('fromUserId', '==', uid), where('status', '==', 'draft'));

            const [sentSnap, recvSnap, draftSnap, sentEndSnap, recvEndSnap] = await Promise.all([
                getDocs(sentQ), getDocs(recvQ), getDocs(draftQ),
                // endDate 없는 구문서는 이 쿼리에 안 잡힘 — createdAt 쿼리가 커버. 인덱스 (from/toUserId, endDate DESC)
                getDocs(sentEndQ).catch(e => { console.error('기간 기준 조회 실패(작성일 기준만 표시):', e?.code || e?.message); partialFail = true; return { docs: [] }; }),
                getDocs(recvEndQ).catch(e => { console.error('기간 기준 조회 실패(작성일 기준만 표시):', e?.code || e?.message); partialFail = true; return { docs: [] }; }),
            ]);

            const all = [];
            const idSet = new Set();

            const pushSent = (d) => {
                if (idSet.has(d.id)) return;
                const data = d.data();
                if (data.deletedBySender) return;   // 발행처(내)가 삭제한 발행본 — 보낸 목록에서 숨김 (2026-08-26)
                idSet.add(d.id);
                all.push({ id: d.id, ...data, direction: 'sent' });
            };
            sentSnap.docs.forEach(pushSent);
            sentEndSnap.docs.forEach(pushSent);
            draftSnap.docs.forEach(pushSent);
            const pushRecv = (d) => {
                if (!idSet.has(d.id)) {
                    const data = d.data();
                    if (data.status !== 'draft') {
                        // 수신본은 deletedBySender여도 유지 — "발행처에서 삭제함" 배지 + [목록에서 제거]로 정리
                        idSet.add(d.id);
                        all.push({ id: d.id, ...data, direction: 'received' });
                    }
                }
            };
            recvSnap.docs.forEach(pushRecv);
            recvEndSnap.docs.forEach(pushRecv);

            all.sort((a, b) => {
                const at = a.createdAt?.toDate?.() || new Date(0);
                const bt = b.createdAt?.toDate?.() || new Date(0);
                return bt - at;
            });

            setStatements(all);
            setLoadError(partialFail ? 'partial' : null);
        } catch (e) {
            console.error('거래명세서 로드 실패:', e);
            setStatements([]);   // 이전 기간 목록·합계가 남아 보이지 않게
            setLoadError('full');
        }
        setLoading(false);
    };

    // ── 월 마감 현황 보드: 거래처별 의뢰서/자동수집/명세서 상태 집계 ──
    const isLabUser = ['lab', 'outsource'].includes(user?.businessType || user?.companyBusinessType || '');
    const isClinicUser = ['dental', 'clinic'].includes(user?.businessType || user?.companyBusinessType || '');
    // 결제 전 기공소(제한 모드) = 기존 명세서 읽기 전용 (계획_가입승인결제흐름 W6, 법무 R4):
    //   목록·상세·미리보기·인쇄·PDF·엑셀 내려받기는 그대로, 작성·이어쓰기·발행·수정·삭제·수집함은 숨김
    // 결제하지 않은 치과(clinic_v62_locked·clinic_locked)도 같은 읽기 전용 (대표 10-15 "응") — 정산 확인·수정 요청·목록에서 제거 숨김
    const readOnly = readOnlyProp === true || ['lab_limited', 'clinic_v62_locked', 'clinic_locked'].includes(user?.accessMode);

    // ── 거래명세서 1단계 (2026-10-23): 요약 카드·마감 초안·입금 열 ──
    // 기공소 대표 계정에서만(치과·직원 X — 기존 판정 isLabUser·excelExport.isLabStaff 재사용). 읽기 전용(결제 전)도 숨김.
    const isLabOwner = isLabUser && !isLabStaff(user) && !readOnly;
    const ownerLabId = user?.companyId || user?.uid || '';

    // 작성 화면을 지정 상태로 연다 (마감 초안·[이번 초안에 넣기]·초안 열기 공용)
    const openCreate = ({ statementId = null, clinicId = null, from = '', to = '', autoDraft = null } = {}) => {
        setCorrectFromId(null);
        setEditDraftId(statementId);
        setCreateClinicId(clinicId);
        setCreateFrom(from || '');
        setCreateTo(to || '');
        setCreateAutoDraft(autoDraft);
        setCurrentView('create');
    };
    const openDraft = (id) => openCreate({ statementId: id });

    // [초안 만들기] — 명세 §3-4 충돌 4경우: 없음=새로 / 같은 기간=이어쓰기 / 다른 기간=막고 열기 안내 / 정정 중=막고 열기 안내
    const handleClosingAction = (partnerKey, partnerName, c) => {
        const who = partnerName || '이 치과';
        if (!c) return;
        if (c.state === 'noClosingDay') {
            if (window.confirm(`${who}는 청구 마감일이 정해지지 않았습니다.\n거래처 화면에서 청구 마감일을 정하면 마감 초안을 한 번에 만들 수 있습니다.\n\n지금은 작성 화면을 열어 직접 작성할까요?`)) {
                openCreate({ clinicId: c.clinicId || null });
            }
            return;
        }
        if (c.state === 'issued') { alert(`${who} ${c.period.label}은 이미 발행됐습니다.`); return; }
        if (c.state === 'otherDraft' || c.state === 'correction') {
            if (window.confirm(`${who}: ${closingBlockedText(c)}.\n기간이 다른 내용을 한 장에 섞지 않도록 새 초안은 만들지 않았습니다.\n\n그 명세서를 열까요?`)) openDraft(c.statementId);
            return;
        }
        openCreate({
            statementId: c.state === 'sameDraft' ? c.statementId : null,
            clinicId: c.clinicId || null,
            from: c.period.from, to: c.period.to,
            autoDraft: closingAutoDraft(partnerKey, partnerName, c),
        });
    };

    // 수집함 [이 치과 초안 만들기] → navigate('/transactions', { state: { closingDraftFor: { partnerKey, partnerName } } })
    //   한 번만 실행하고 state 는 지운다(새로고침해도 다시 실행 안 됨)
    //   목록이 이미 열려 있는 상태(수집함 화면이 목록 안에서 열림)에서 와도 받도록 location 변화를 본다
    const closingForRef = useRef(null);
    const [closingReqTick, setClosingReqTick] = useState(0);
    useEffect(() => {
        if (location.state?.closingDraftFor) {
            closingForRef.current = location.state.closingDraftFor;
            navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: null });
            if (currentView === 'inbox') setCurrentView('list');   // 수집함에서 넘어오면 목록으로 (막힘 안내 후 목록에 남도록)
            setClosingReqTick(t => t + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);
    useEffect(() => {
        const req = closingForRef.current;
        if (!req || !user) return;
        closingForRef.current = null;
        if (!isLabOwner) { alert('마감 초안은 기공소 대표 계정에서만 만들 수 있습니다.'); return; }
        if (!req.partnerKey) { console.warn('마감 초안 요청에 거래처 키 없음'); alert('어느 치과인지 알 수 없어 초안을 만들지 못했습니다.'); return; }
        (async () => {
            try {
                const plan = await planClosingFor(ownerLabId, req.partnerKey);
                handleClosingAction(req.partnerKey, req.partnerName, plan);
            } catch (e) {
                console.error('마감 초안 준비 실패:', e?.code || e?.message);
                alert('초안을 준비하지 못했습니다. 잠시 후 다시 시도해 주세요.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isLabOwner, closingReqTick]);

    // ① [이번 초안에 넣기] — 그 치과 작성 화면을 그 작업월로 열고, 그 행의 케이스만 [담기]와 같은 경로로 담아 임시저장.
    //   명세 §3-4: 같은 기간(그 달) 작성 중 초안이면 이어쓰기, 기간이 다른 초안·정정 중이면 막고 [그 명세서 열기] 안내 (P1-1)
    const handleAddUnbilledToDraft = async (row) => {
        if (addingUnbilledRef.current) return;   // 연타 방지
        const ymd = String(row.workDate || (row.monthKey ? `${row.monthKey}-01` : '')).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) { alert('작업 날짜를 알 수 없어 자동으로 담지 못했습니다. 작성 화면에서 직접 담아 주세요.'); return; }
        const [y, m] = ymd.split('-').map(Number);
        const from = `${y}-${String(m).padStart(2, '0')}-01`;
        const to = `${y}-${String(m).padStart(2, '0')}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
        const caseIds = (row.caseIds || []).map(x => String(x).replace(/^case:/, '')).filter(Boolean);
        if (caseIds.length === 0) { alert('담을 작업을 찾지 못했습니다.'); return; }
        addingUnbilledRef.current = true;
        try {
            const stmts = await loadStatementsForClosing(ownerLabId);
            const drafts = stmts.filter(st => st.status === 'draft' && partnerKeyOfStatement(st) === row.partnerKey);
            const correction = drafts.find(d => d.correctsId);
            if (correction) {
                if (window.confirm(`${row.clinicName}: 고쳐서 다시 발행 중인 명세서가 있어 자동으로 넣지 않았습니다.\n\n그 명세서를 열까요?`)) openDraft(correction.id);
                return;
            }
            const same = drafts.find(d => kstYmd(d.startDate) === from && kstYmd(d.endDate) === to);
            const other = !same && drafts[0];
            if (other) {
                const om = Number(String(kstYmd(other.endDate) || kstYmd(other.startDate)).slice(5, 7)) || null;
                if (window.confirm(`${row.clinicName}: ${om ? `${om}월분` : '다른 기간'} 작성 중 명세서가 아직 안 보내졌습니다.\n기간이 다른 내용을 한 장에 섞지 않도록 자동으로 넣지 않았습니다.\n\n그 명세서를 열까요?`)) openDraft(other.id);
                return;
            }

            // 지워진 명세서에 묶여 'imported'로 남은 케이스만 pending 으로 되돌린다 (P2-③)
            //   - 지금 [제외] 상태면 건너뜀 · 묶인 명세서가 실제로 없거나 삭제 표시(deletedBySender)일 때만
            if (row.wasInStatement) {
                const stmtLive = new Map();
                const toRestore = [];
                for (const cid of caseIds.slice(0, 450)) {
                    const cs = await getDoc(doc(db, 'parsedCases', cid));
                    if (!cs.exists()) continue;
                    const c = cs.data();
                    if (c.status === 'excluded' || !c.statementId) continue;
                    if (!stmtLive.has(c.statementId)) {
                        const ss = await getDoc(doc(db, 'transactionStatements', c.statementId));
                        stmtLive.set(c.statementId, ss.exists() && ss.data().deletedBySender !== true);
                    }
                    if (!stmtLive.get(c.statementId)) toRestore.push(cid);
                }
                if (toRestore.length > 0) {
                    const b = writeBatch(db);
                    toRestore.forEach(cid => b.update(doc(db, 'parsedCases', cid), { status: 'pending', statementId: deleteField() }));
                    await b.commit();
                    console.log('청구 안 된 작업 되돌림:', row.parsedCaseId, '케이스', toRestore.length);   // id·건수만
                }
            }

            const auto = { mode: 'unbilled', partnerKey: row.partnerKey, partnerName: row.clinicName, from, to, collectFrom: from, collectTo: to, caseIds };
            if (same) {
                openCreate({ statementId: same.id, autoDraft: auto });
            } else {
                const withId = stmts.find(st => partnerKeyOfStatement(st) === row.partnerKey && st.toUserId);
                const clinicId = row.clinicId || (withId ? withId.toUserId : (isMemberPartnerKey(row.partnerKey) ? row.partnerKey : null));
                openCreate({ clinicId, from, to, autoDraft: auto });
            }
        } catch (e) {
            console.error('이번 초안에 넣기 실패:', row.parsedCaseId, e?.code || e?.message);
            alert('초안에 넣을 준비를 하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            addingUnbilledRef.current = false;
        }
    };

    // ── 온보딩 체크리스트 데이터: 기공소이고 명세서 0건일 때만 조회 ──
    // ① 에이전트 설치 = agentStatus/{labId} 하트비트 (ParsedCasesInbox와 동일한 판정)
    // ② 폴더 연결·스캔 = 수집 대기(inboxPendingCount) 또는 parsedCases 이력 존재
    useEffect(() => {
        if (!isLabUser || loading || statements.length > 0) return;
        const labId = user?.companyId || user?.uid;
        if (!labId) return;
        let alive = true;
        getDoc(doc(db, 'agentStatus', labId))
            .then(snap => {
                if (!alive) return;
                const d = snap.exists() ? snap.data() : null;
                const devices = d ? Object.values(d.devices || {}) : [];
                setAgentConnected(devices.length > 0 || !!d?.lastSeen);
            })
            .catch(() => { if (alive) setAgentConnected(false); });
        getDocs(query(collection(db, 'parsedCases'), where('labId', '==', labId), limit(1)))
            .then(snap => { if (alive) setHasParsedCases(!snap.empty); })
            .catch(() => { /* 이력 조회 실패 시 미완료로 표시 */ });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLabUser, loading, statements.length]);
    // (2026-08-26 성능) 보드 UI는 숨김({false && ...}) 상태인데 workOrders 전량·partnerships 조회가
    // 필터 변경마다 돌았다 — 수집함 배지에 필요한 pending parsedCases 카운트만 남긴다.
    // 옛 보드 집계 로직(거래처별 의뢰서/수집/명세서/수수료)은 아래 주석에 보존 — 보드를 되살릴 때 복원.
    /*
    const fetchBoard_old = async () => {
        ...
        const [ordersSnap, parsedSnap, partnershipSnap] = await Promise.all([
            getDocs(query(collection(db, 'workOrders'), where('toUserId', '==', myId))),
            getDocs(query(collection(db, 'parsedCases'), where('labId', '==', myId), where('status', '==', 'pending'))),
            getDocs(query(collection(db, 'partnerships'), where('labId', '==', myId), where('status', '==', 'active'))),
        ]);
        // orderCount(기간 내 의뢰서 수), parsedCount(거래처별 수집 대기), matchedSet(commissionStartDate 있는 파트너십)
        // → partners.map으로 rows 구성(orders/parsedPending/draftId/issued/settled/matched) 후 setBoardRows(rows)
    };
    */
    const fetchBoard = async () => {
        if (!isLabUser) { setBoardRows([]); return; }
        try {
            const myId = user?.companyId || user?.uid;
            const pSnap = await getDocs(query(collection(db, 'parsedCases'), where('labId', '==', myId), where('status', '==', 'pending')));
            // 스캔만 있는 폴더(sourceType scan_only)는 명세서 대상이 아니므로 배지에서 제외 (2026-08-08)
            setInboxPendingCount(pSnap.docs.filter(d => d.data().sourceType !== 'scan_only').length);
        } catch (e) {
            console.warn('수집함 배지 집계 실패:', e);   // 배지 실패도 흔적은 남긴다 (조용한 실패 금지)
        }
        setBoardRows([]);
    };

    useEffect(() => {
        fetchBoard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isLabUser]);

    // ── 일괄 draft 생성: 기간 내 의뢰서가 있는데 명세서가 없는 거래처마다 임시저장 자동 생성 ──
    // 작성 화면과 동일한 공용 로직(utils/statementItems.js)으로 항목·단가를 구성한다. 발행은 하지 않음.
    const createDraftsForAll = async () => {
        const myId = user?.companyId || user?.uid;
        // 대상: 의뢰서 있음 + 임시저장/발행 없음 + 덴클 회원 거래처 (이메일 매핑 거래처는 수동 작성)
        const targets = boardRows.filter(r => r.orders > 0 && !r.draftId && !r.issued && !r.id.startsWith('email_'));
        if (targets.length === 0) {
            alert('일괄 생성할 거래처가 없습니다.\n(기간 내 의뢰서가 있고 명세서 미작성인 덴클 거래처가 대상입니다)');
            return;
        }
        const { start, end, label } = getPeriod();
        const confirmMsg = `${label} 의뢰서 기준으로 ${targets.length}개 거래처의 임시저장 명세서를 생성할까요?\n\n`
            + targets.map(t => `· ${t.name} (의뢰서 ${t.orders}건)`).join('\n')
            + '\n\n각 거래처 단가표가 자동 적용되며, 발행 전 [이어서 작성]에서 검토·수정할 수 있습니다.\n거래처에는 발송되지 않습니다.';
        if (!window.confirm(confirmMsg)) return;

        setBatchCreating(true);
        let ok = 0;
        const skipped = [];
        try {
            const senderDoc = await getDoc(doc(db, 'users', myId));
            const senderData = senderDoc.exists() ? senderDoc.data() : {};
            // 계좌는 paymentProfiles(비공개)에서 — users 노출 제거 (2026-08-06 보안 P1②)
            const senderBank = await fetchDomesticBank(myId, senderData);
            const endInclusive = new Date(end.getTime() - 24 * 3600 * 1000);   // end는 미포함 경계

            for (const t of targets) {
                try {
                    const snap = await getDocs(query(
                        collection(db, 'workOrders'),
                        where('toUserId', '==', myId),
                        where('fromUserId', '==', t.id)
                    ));
                    const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                        .filter(o => {
                            const dt = toJsDate(dateBasis === 'dueDate' ? o.dueDate : o.createdAt);
                            return dt && !isNaN(dt) && dt >= start && dt < end;
                        })
                        .sort((a, b) => {
                            const ta = toJsDate(dateBasis === 'dueDate' ? a.dueDate : a.createdAt)?.getTime() || 0;
                            const tb = toJsDate(dateBasis === 'dueDate' ? b.dueDate : b.createdAt)?.getTime() || 0;
                            return ta - tb;
                        });
                    if (orders.length === 0) { skipped.push(`${t.name}(기간 내 의뢰서 없음)`); continue; }

                    let priceList = [];
                    try {
                        const plSnap = await getDoc(doc(db, 'priceLists', `${myId}_${t.id}`));
                        if (plSnap.exists()) priceList = normalizePriceItems(plSnap.data().items);
                    } catch { /* 단가표 없으면 0원 항목으로 생성 — 검토 단계에서 입력 */ }

                    const stmtItems = buildItemsFromOrders(orders, { priceList });
                    const subtotal = stmtItems.reduce((s, it) => s + (it.toothCount || 0) * (it.unitPrice || 0), 0);
                    const totalTeeth = stmtItems.reduce((s, it) => s + (it.toothCount || 0), 0);

                    await addDoc(collection(db, 'transactionStatements'), {
                        statementNumber: generateStatementNumber(),
                        fromUserId: myId,
                        fromUserName: senderData.businessName || senderData.name || '',
                        supplierInfo: {
                            businessName: senderData.businessName || '',
                            businessNumber: senderData.businessNumber || '',
                            representativeName: senderData.representativeName || '',
                            address: senderData.address || '',
                            phone: senderData.businessPhone || senderData.phone || '',
                            bankAccount: senderBank || null,
                        },
                        toUserId: t.id,
                        toUserName: t.name,
                        isManual: false,
                        startDate: start,
                        endDate: endInclusive,
                        dateBasis,
                        items: serializeStatementItems(stmtItems),
                        subtotal,
                        totalDiscount: 0,
                        discountType: 'amount',
                        discountAmount: 0,
                        totalAmount: subtotal,
                        totalTeeth,
                        notes: '',
                        createdAt: new Date(),
                        createdBy: user?.uid || myId,
                        status: 'draft',
                        batchCreated: true,   // 일괄 생성 표시
                    });
                    ok++;
                } catch (e) {
                    console.error('일괄 생성 실패:', t.name, e);
                    skipped.push(`${t.name}(오류)`);
                }
            }
        } finally {
            setBatchCreating(false);
        }
        alert(`임시저장 ${ok}건 생성 완료${skipped.length > 0 ? `\n건너뜀: ${skipped.join(', ')}` : ''}\n\n각 거래처의 [이어서 작성]에서 검토 후 발행하세요.`);
        fetchStatements();   // 보드/목록 갱신
    };

    // ── CMS 수금 요청 (기공소 → 치과): 희망 출금일 지정, 치과가 계좌 등록으로 동의하면 자동 수금 ──
    const requestCms = async (t) => {
        const dayStr = window.prompt(
            `${t.name}에 CMS 자동출금(수금) 동의를 요청합니다.\n\n희망 출금일을 입력하세요 (1~28 사이 숫자).\n치과가 동의하면 CMS사 연동 완료 후 매월 해당일에 확정 명세서 금액이 자동 출금·정산됩니다.\n(현재 자동출금 시스템 연동 준비 중 — 연동 완료 전까지는 실제 출금이 발생하지 않습니다)`,
            '5'
        );
        if (dayStr == null) return;
        const day = parseInt(dayStr, 10);
        if (isNaN(day) || day < 1 || day > 28) { alert('출금일은 1~28 사이 숫자로 입력해주세요.'); return; }
        const myId = user?.companyId || user?.uid;
        const myName = user?.businessName || user?.companyName || user?.name || '기공소';
        try {
            await setDoc(doc(db, 'cmsRequests', `${myId}_${t.id}`), {
                labId: myId,
                labName: myName,
                clinicId: t.id,
                clinicName: t.name,
                withdrawalDay: day,
                status: 'pending',
                requestedAt: new Date(),
            }, { merge: true });
            await createNotification({
                recipientId: t.id,
                type: 'cms_request',
                title: 'CMS 자동출금 동의 요청',
                message: `${myName}에서 매월 ${day}일 기공료 자동출금(CMS) 동의를 요청했습니다.\n[정산·수취 확인] 메뉴에서 출금계좌를 등록하면 동의가 완료됩니다.`,
                link: '/clinic/settlement',
            });
            alert(`${t.name}에 CMS 수금 요청을 보냈습니다. (매월 ${day}일)\n치과가 출금계좌 등록으로 동의하면 CMS사 연동 완료 후 정한 날짜에 자동 수금이 시작됩니다.`);
        } catch (e) {
            console.error('CMS 요청 실패:', e);
            alert('요청 전송에 실패했습니다.');
        }
    };

    // [세무 자료 내려받기] — 선택 월의 명세서·계산서를 엑셀 1개(시트 3장)로. 실패·0건은 알림 (조용한 실패 금지)
    const handleExportTax = async () => {
        if (exportingTax) return;
        setExportingTax(true);
        try {
            const r = await exportTaxMonthlyReport(user, taxMonth);
            if (r.statementCount === 0 && r.invoiceCount === 0) {
                alert(`${taxMonth.replace('-', '년 ')}월에는 발행된 명세서·세금계산서가 없습니다.\n(작성 중인 문서는 포함되지 않습니다)`);
            } else {
                alert(`엑셀 파일을 내려받았습니다.\n${r.fileName}\n\n거래명세서 ${r.statementCount}건 · 세금계산서 ${r.invoiceCount}건`);
            }
        } catch (e) {
            if (e?.message === LAB_STAFF_EXPORT_MSG) { alert(LAB_STAFF_EXPORT_MSG); return; }   // 권한 안내(오류 아님)
            console.error('세무 자료 내려받기 실패:', e);
            alert(`세무 자료를 만들지 못했습니다.\n${e?.message || e?.code || ''}\n잠시 후 다시 시도해주세요.`);
        } finally {
            setExportingTax(false);
        }
    };
    // [주문내역 내려받기] — 기간 내 웹 의뢰서를 치아 1개=1행으로. 수집 자동등록·이메일·외주·취소/거절 제외
    const handleExportOrders = async () => {
        if (exportingOrders) return;
        setExportingOrders(true);
        try {
            const r = await exportOrderHistory(user, orderFrom, orderTo);
            if (r.orderCount === 0) {
                alert(`${orderFrom} ~ ${orderTo} 기간에 웹으로 주고받은 의뢰서가 없습니다.\n(프로그램 수집분·이메일·외주·취소된 의뢰서는 포함되지 않습니다)`);
            } else {
                alert(`엑셀 파일을 내려받았습니다.\n${r.fileName}\n\n의뢰서 ${r.orderCount}건 · 치아 ${r.toothCount}개`);
            }
        } catch (e) {
            if (e?.message === LAB_STAFF_EXPORT_MSG) { alert(LAB_STAFF_EXPORT_MSG); return; }   // 권한 안내(오류 아님)
            console.error('주문내역 내려받기 실패:', e);
            alert(`주문내역을 만들지 못했습니다.\n${e?.message || e?.code || ''}\n잠시 후 다시 시도해주세요.`);
        } finally {
            setExportingOrders(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...statements];

        if (selectedPartner !== 'all') {
            filtered = filtered.filter(s => {
                const isSent = s.direction === 'sent';
                return isSent ? s.toUserId === selectedPartner : s.fromUserId === selectedPartner;
            });
        }

        if (partyChip !== 'all') filtered = filtered.filter(s => (OUTSOURCE_KINDS.includes(s.kind) ? 'outsource' : 'clinic') === partyChip);

        // 작성 중(draft)은 상단 고정 섹션에서 기간 무관 표시 — 본 목록은 발행본만 (2026-08-26)
        if (statusChip === 'draft') {
            filtered = filtered.filter(s => s.status === 'draft');
        } else {
            filtered = filtered.filter(s => s.status !== 'draft');
            const { start, end } = getPeriod();
            filtered = filtered.filter(s => {
                const inRange = (d) => d && !isNaN(d.getTime?.() ?? NaN) && d >= start && d < end;
                if (dateBasis === 'dueDate') return inRange(toJsDate(s.endDate));
                // 작성일 기준이어도 기간 마감(endDate)이 그 달이면 함께 표시 (2026-08-31) —
                // 묵힌 draft를 이어써 발행하면 작성일이 기간보다 몇 달 이르다 (실사례: 우리미소 8월분, 작성일 5/29).
                // "8월 것"을 8월 화면에서 못 찾는 것보다 두 달에 걸쳐 보이는 쪽을 택한다.
                return inRange(s.createdAt?.toDate?.() || (s.orderDate?.toDate?.() ?? null)) || inRange(toJsDate(s.endDate));
            });
            // '수정 요청 중' = 회원 치과의 disputed + 공유 링크 수정 요청(확인 전) (2026-10-24 재점검 P2-6)
            if (statusChip === 'settled') filtered = filtered.filter(s => s.settlementStatus === 'settled');
            else if (statusChip === 'disputed') filtered = filtered.filter(s => s.settlementStatus === 'disputed' || (s.settlementStatus !== 'settled' && hasLinkRevision(s)));
            else if (statusChip === 'issued') filtered = filtered.filter(s => s.settlementStatus !== 'settled' && s.settlementStatus !== 'disputed' && !hasLinkRevision(s));
        }

        setFilteredStatements(filtered);
    };

    // 발행처가 삭제한 수신본 — 수신자가 목록에서 하드 삭제로 정리 (rules: deletedBySender==true일 때만 수신자 delete 허용)
    const removeDeletedReceived = async (e, s) => {
        e.stopPropagation();
        if (deletingId) return;
        // 정산 완료본은 받는 쪽 하드삭제 금지 (규칙 2026-10-23) — 버튼도 숨기지만 이중 방어
        if (s.settlementStatus === 'settled') return;
        // (P2-3, 2026-08-26) 세금계산서 연결 가드 — 발행자 삭제 가드와 대칭
        const canceledLikeTax = ['canceled', 'cancelled', 'voided'];
        if (s.taxInvoiceId && !canceledLikeTax.includes(s.taxInvoiceStatus)) {
            alert('이 명세서에 연결된 세금계산서가 있습니다.\n[세금계산서] 메뉴에서 계산서를 먼저 정리(취소)한 뒤 제거해주세요.');
            return;
        }
        const taxNote = s.taxInvoiceId ? '\n※ 연결됐던 세금계산서는 취소 상태로 확인됐습니다.' : '';
        if (!window.confirm(`${s.correctedTo ? '정정된 명세서입니다 (정정 명세서가 새로 발행됨).' : '발행처에서 삭제한 명세서입니다.'}\n내 목록에서 제거할까요? (제거하면 되돌릴 수 없습니다)\n\n발행처: ${s.fromUserName || '-'}\n번호: ${s.statementNumber || '-'}${taxNote}`)) return;
        setDeletingId(s.id);
        try {
            await deleteDoc(doc(db, 'transactionStatements', s.id));
            setStatements(prev => prev.filter(x => x.id !== s.id));
        } catch (err) {
            console.error('목록에서 제거 실패:', err);
            alert(`목록에서 제거하지 못했습니다. (오류: ${err?.code || err?.message || '알 수 없음'})\n잠시 후 다시 시도해주세요.`);
        } finally {
            setDeletingId(null);
        }
    };

    const handleBackToList = () => {
        setCurrentView('list');
        setSelectedStatementId(null);
        setEditDraftId(null);
        setCorrectFromId(null);
        setCreateClinicId(null);
        setCreateFrom('');
        setCreateTo('');
        setCreateAutoDraft(null);
        fetchStatements();
    };

    const formatDate = (ts) => {
        if (!ts) return '-';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    const formatCurrency = (amount) => {
        return (amount || 0).toLocaleString();
    };

    // 용어 통일 (2026-10-24 대표 결정): 작성 중 / 발행됨 / 수정 요청 중 / 정산 완료
    const getStatusBadge = (s) => {
        // 주문 거래명세서: status = draft | confirmed, settlementStatus = pending | disputed | settled
        if (s.status === 'draft') {
            return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#eef2ff', color: '#4338ca' }}>작성 중</span>;
        }
        if (s.settlementStatus === 'settled') {
            return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#f0fdf4', color: '#15803d' }}>정산 완료</span>;
        }
        if (s.settlementStatus === 'disputed') {
            return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#fef2f2', color: '#dc2626' }}>수정 요청 중</span>;
        }
        // 공유 링크(비회원 치과)로 온 수정 요청 — 치과가 [확인]을 누르기 전까지 (P2-6)
        if (hasLinkRevision(s)) {
            return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#fef2f2', color: '#dc2626' }}>수정 요청 중(링크)</span>;
        }
        // confirmed (settlementStatus pending 또는 미지정)
        return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#fff7e8', color: '#b45309' }}>발행됨</span>;
    };


    // 미리보기 대상 = 현재 필터·정렬된 목록 중 발행본(작성 중 제외). 행 클릭(상세·이어쓰기)은 건드리지 않는다
    const previewables = filteredStatements.filter(s => s.status !== 'draft');
    const previewIdx = previewId ? previewables.findIndex(s => s.id === previewId) : -1;
    const previewStatement = previewIdx >= 0 ? previewables[previewIdx] : null;
    const openPreview = (e, s) => {
        e.stopPropagation();   // 행 클릭(상세 이동)과 분리
        setPreviewId(s.id);
    };
    // [정산 처리하러 가기] 노출: 수신자이고 아직 정산 전(pending/disputed)이며 발행처 삭제본이 아닐 때
    const canGoDetailFromPreview = (s) => !!s && s.direction === 'received' && !s.deletedBySender && s.status === 'confirmed'
        && (!s.settlementStatus || s.settlementStatus === 'pending' || s.settlementStatus === 'disputed');
    const previewButton = (s) => (
        <button
            onClick={(e) => openPreview(e, s)}
            title="발행된 명세서를 그대로 봅니다 (정산 처리는 행을 눌러 여세요)"
            aria-label="미리보기"
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 22, marginLeft: 4, borderRadius: 6,
                border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', cursor: 'pointer', verticalAlign: 'middle', padding: 0,
            }}>
            <Eye size={13} />
        </button>
    );

    // '입금' 열 — 대표 화면에서 원장을 불러왔을 때만
    const showPayCol = isLabOwner && !!recvByPartner;

    const thStyle = {
        padding: '12px 10px', textAlign: 'left', fontSize: '12px',
        fontWeight: '700', color: '#64748b', whiteSpace: 'nowrap',
    };
    const tdStyle = {
        padding: '12px 10px', fontSize: '13px', color: '#334155',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    };

    // 정산 완료본 [고쳐서 다시 발행] — 작성 화면을 correctFromId로 연다 (statementId 없음 = 새 명세서) (2026-10-23)
    if (currentView === 'create' && !readOnly && correctFromId) {
        return <CreateTransactionStatement user={user} onBack={handleBackToList} correctFromId={correctFromId} />;
    }
    if (currentView === 'create' && !readOnly) {
        return <CreateTransactionStatement user={user} onBack={handleBackToList} statementId={editDraftId}
            initialClinicId={createClinicId} initialFrom={createFrom} initialTo={createTo} autoDraft={createAutoDraft} />;
    }
    if (currentView === 'inbox' && !readOnly) {
        return <ParsedCasesInbox user={user}
            onBack={() => { setCurrentView('list'); fetchStatements(); }}
            onCreateStatement={({ clinicId, from, to }) => {
                setEditDraftId(null);
                setCreateClinicId(clinicId);
                setCreateFrom(from || '');
                setCreateTo(to || '');
                setCurrentView('create');
            }} />;
    }
    if (currentView === 'detail' && selectedStatementId) {
        return <StatementErrorBoundary onBack={handleBackToList}>
            <ViewTransactionStatement statementId={selectedStatementId} user={user} onBack={handleBackToList} readOnly={readOnly}
                onEdit={(id) => { setEditDraftId(id); setCreateClinicId(null); setCurrentView('create'); }}
                onCorrect={(id) => { setEditDraftId(null); setCreateClinicId(null); setCorrectFromId(id); setCurrentView('create'); }} />
        </StatementErrorBoundary>;
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* 제목 (2026-10-23 §5-1) — 치과(받는 쪽) 계정은 기공소용 부제 대신 받는 쪽 문구 */}
            <PageHeader
                title="거래명세서"
                subtitle={isClinicUser ? '기공소에서 받은 명세서를 확인합니다' : '치과에 보낼 명세서를 만들고 받을 돈을 봅니다'}
            />

            {/* 결제 전 기공소(제한 모드) 안내 — 기존 자료는 보기·인쇄·내려받기 가능 (W6, 법무 R4) */}
            {readOnly && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#475569', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>{isClinicUser
                        ? '지금은 보기 전용입니다. 받은 거래명세서는 열람·인쇄·내려받기할 수 있고, 정산 확인·수정 요청은 구독 후 이용할 수 있습니다.'
                        : '지금은 보기 전용입니다. 기존 거래명세서는 열람·인쇄·내려받기할 수 있고, 새로 작성·발행하려면 구독이 필요합니다.'}</span>
                    <button onClick={() => navigate('/subscription')}
                        style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 700, border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', cursor: 'pointer' }}>
                        {'구독 안내 보기'}
                    </button>
                </div>
            )}

            {/* 상단 버튼 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px' }}>
                {onBackToSettlement && (
                    <button
                        onClick={onBackToSettlement}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px', background: '#f1f5f9', color: '#64748b',
                            border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        정산 관리로
                    </button>
                )}
                {/* [수집함] 버튼·배지 → '더 보기' 안 "수집 원본 보기"로 이동 (2026-10-23 1단계 §5-1 — 필요한 숫자는 카드 ①이 대신) */}
                {/* 명세서 발행은 기공소만 — 치과는 받는 쪽이다 (대표 확정 2026-08-09: 치과가 명세서를 만들 필요 없음) */}
                {isLabUser && !readOnly && (
                    <button
                        onClick={() => setCurrentView('create')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px', background: '#6366f1', color: 'white',
                            border: 'none', borderRadius: '8px', cursor: 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        <Plus size={18} /> 새 명세서
                    </button>
                )}
                {/* [내려받기 ▾] — 엑셀 2종을 메뉴 하나로 접음 (2026-10-23 §5-1, 내용·동작 동일) */}
                <button
                    onClick={() => setDownloadOpen(o => !o)}
                    aria-expanded={downloadOpen}
                    title="세무사에게 보낼 월별 자료 · 보철 주문내역 엑셀"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 16px', background: downloadOpen ? '#e2e8f0' : '#f1f5f9', color: '#475569',
                        border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
                        fontWeight: '600', fontSize: '13px',
                    }}
                >
                    <FileSpreadsheet size={15} /> {downloadOpen ? '내려받기 ▴' : '내려받기 ▾'}
                </button>
            </div>

            {/* 엑셀 내려받기 2종 (2026-09-04) — 세무사에게 보낼 월별 자료 / 보철 주문내역. [내려받기 ▾]를 눌렀을 때만 펼침 */}
            {downloadOpen && <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '10px 24px', alignItems: 'center',
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                padding: '10px 14px', marginBottom: '20px', fontSize: '13px', color: '#475569',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#334155' }}>세무사에게 보낼 월별 자료</span>
                    <input
                        type="month"
                        value={taxMonth}
                        onChange={(e) => setTaxMonth(e.target.value)}
                        style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                        onClick={handleExportTax}
                        disabled={exportingTax || !taxMonth}
                        title="선택한 달의 거래명세서·세금계산서를 엑셀 파일 1개(시트 3장)로 내려받습니다 — 환자 정보는 포함되지 않습니다"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '7px 14px', background: exportingTax ? '#e2e8f0' : '#f1f5f9', color: '#475569',
                            border: '1px solid #e2e8f0', borderRadius: '8px', cursor: exportingTax ? 'wait' : 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        <FileSpreadsheet size={15} /> {exportingTax ? '만드는 중…' : '세무 자료 내려받기'}
                    </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#334155' }}>보철 주문내역(웹 의뢰서)</span>
                    <input type="date" value={orderFrom} onChange={(e) => setOrderFrom(e.target.value)}
                        style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    <span>~</span>
                    <input type="date" value={orderTo} onChange={(e) => setOrderTo(e.target.value)}
                        style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    <button
                        onClick={handleExportOrders}
                        disabled={exportingOrders || !orderFrom || !orderTo}
                        title="기간 내 웹 의뢰서를 치아 1개당 1줄로 엑셀에 담습니다 (프로그램 수집분·이메일·외주·취소 제외)"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '7px 14px', background: exportingOrders ? '#e2e8f0' : '#f1f5f9', color: '#475569',
                            border: '1px solid #e2e8f0', borderRadius: '8px', cursor: exportingOrders ? 'wait' : 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        <FileSpreadsheet size={15} /> {exportingOrders ? '만드는 중…' : '주문내역 내려받기'}
                    </button>
                </div>
            </div>}

            {/* 요약 카드 3장 (2026-10-23 1단계 §5-1) — 기공소 대표만. 목록을 먼저 그린 뒤(ready) 뒤에서 계산 */}
            {isLabOwner && ownerLabId && (
                <SummaryCards
                    labId={ownerLabId}
                    ready={!loading}
                    refreshKey={summaryRefresh}
                    onClosingAction={handleClosingAction}
                    onOpenDraft={openDraft}
                    onAddToDraft={handleAddUnbilledToDraft}
                    onReceivables={setRecvByPartner}
                    onGoPartners={() => navigate('/partners')}
                />
            )}

            {/* 💰 치과 「기간으로 예상 기공료 보기」 카드 (2026-10-07) — 기존 월별/기간 필터와 별도(기공소 1곳 선택이 필요). 기공소 화면에는 없음.
                플래그 OFF(기본)면 비노출. ☆ 캡션 C-1 · 안내 문구는 services/expectedStatementApi.js 상수(법무 §3) */}
            {isClinicUser && isClinicExpectedStatementOn(features) && (() => {
                const labTypes = ['lab', '기공소', 'outsource'];
                const labs = partners.filter(p => labTypes.includes(p.businessType || '') && !String(p.id).startsWith('email_') && !String(p.id).startsWith('agent_'));
                const selectedLab = expLabId || (labs.length === 1 ? labs[0].id : '');
                const openAt = getClinicExpectedStatementOpenAtText(features);
                const runExpected = async () => {
                    if (expLoading) return;
                    setExpNotice(null);
                    const bad = validateExpectedRange(expFrom, expTo, selectedLab);
                    if (bad) { setExpNotice({ kind: 'error', text: bad }); return; }
                    setExpLoading(true);
                    try {
                        const res = await fetchExpectedStatement({ from: expFrom, to: expTo, labId: selectedLab });
                        if (!res) { setExpNotice({ kind: 'error', text: '예상 명세서를 만들지 못했습니다. 잠시 후 다시 시도해 주세요', retry: true }); return; }
                        if (res.shared === false) { setExpNotice({ kind: 'info', text: EXPSTMT_NOT_SHARED }); return; }   // C-5
                        if (res.summary?.hasPrice === false) { setExpNotice({ kind: 'info', text: EXPSTMT_NO_PRICE }); return; }   // 결정 7 안내만
                        setExpData(res);
                    } catch (e) {
                        const d = describeExpectedStatementError(e);
                        if (d.kind === 'feature-off') {
                            // 웹 플래그 캐시(5분)가 서버보다 늦게 꺼진 경우 — 카드는 그대로, 안내만
                            setExpNotice({ kind: 'feature-off', text: `아직 열리지 않았습니다 (오픈 예정: ${openAt || '곧'})` });
                        } else {
                            setExpNotice({ kind: 'error', text: d.text, retry: d.kind === 'internal' });
                        }
                    } finally {
                        setExpLoading(false);
                    }
                };
                const inputS = { padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' };
                return (
                    <div style={{
                        background: '#FFFDF7', border: '1px solid #FDE68A', borderRadius: '10px',
                        padding: '12px 14px', marginBottom: '20px', fontSize: '13px', color: '#475569',
                    }}>
                        <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 4 }}>기간으로 예상 기공료 보기</div>
                        <div style={{ fontSize: 12.5, color: '#78350F', lineHeight: 1.65, marginBottom: 10 }}>{EXPSTMT_CARD_CAPTION}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <input type="date" value={expFrom} max={expTo} onChange={(e) => setExpFrom(e.target.value)} style={inputS} />
                            <span>~</span>
                            <input type="date" value={expTo} max={toYmd(new Date())} onChange={(e) => setExpTo(e.target.value)} style={inputS} />
                            <select value={selectedLab} onChange={(e) => setExpLabId(e.target.value)} style={{ ...inputS, minWidth: 160 }}>
                                <option value="">기공소 선택</option>
                                {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                            <button
                                onClick={runExpected}
                                disabled={expLoading || labs.length === 0}
                                title={`한 번에 최대 ${EXPSTMT_MAX_DAYS}일까지 볼 수 있습니다 (작성일 기준)`}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '7px 14px', background: expLoading ? '#e2e8f0' : '#D97706', color: expLoading ? '#475569' : '#fff',
                                    border: 'none', borderRadius: '8px', cursor: expLoading ? 'wait' : 'pointer', fontWeight: '700', fontSize: '13px',
                                }}
                            >
                                <Eye size={15} /> {expLoading ? '계산 중…' : '보기'}
                            </button>
                        </div>
                        {labs.length === 0 && (
                            <div style={{ marginTop: 8, fontSize: 12.5, color: '#94a3b8' }}>연결된 기공소가 없습니다. 기공소와 연결되면 여기서 예상 기공료를 볼 수 있습니다.</div>
                        )}
                        {expNotice && (
                            <div style={{
                                marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.6,
                                background: expNotice.kind === 'error' ? '#FEF2F2' : '#F1F5F9',
                                border: `1px solid ${expNotice.kind === 'error' ? '#FECACA' : '#E2E8F0'}`,
                                color: expNotice.kind === 'error' ? '#B91C1C' : '#475569',
                                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            }}>
                                <span>{expNotice.text}</span>
                                {expNotice.retry && (
                                    <button onClick={runExpected} disabled={expLoading}
                                        style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, border: '1px solid #FCA5A5', background: '#fff', color: '#B91C1C', borderRadius: 6, cursor: 'pointer' }}>
                                        다시 시도
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* 온보딩 체크리스트 (기공소 · 명세서 0건) — 빈 화면 대신 3단계 시작 안내 */}
            {isLabUser && !readOnly && !loading && loadError !== 'full' && statements.length === 0 && (() => {
                const step1Done = agentConnected === true;
                const step2Done = inboxPendingCount > 0 || hasParsedCases;
                const stepCircle = (done, n) => (
                    <span style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 800,
                        background: done ? '#dcfce7' : '#eef2ff',
                        color: done ? '#15803d' : '#4338ca',
                        border: done ? '1.5px solid #86efac' : '1.5px solid #c7d2fe',
                    }}>
                        {done ? <Check size={16} strokeWidth={3} /> : n}
                    </span>
                );
                const doneBadge = (
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '3px 10px', borderRadius: 10 }}>
                        완료
                    </span>
                );
                const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 0' };
                return (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>거래명세서 자동화, 3단계면 준비 끝</div>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                            아직 작성된 명세서가 없습니다. 아래 순서대로 진행하면 월말 명세서가 자동으로 만들어집니다.
                        </div>
                        <div style={{ marginTop: 10 }}>
                            {/* ① 에이전트 설치 */}
                            <div style={{ ...rowStyle, borderBottom: '1px solid #f1f5f9' }}>
                                {stepCircle(step1Done, 1)}
                                <div style={{ flex: 1, minWidth: 220 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        덴클 에이전트 설치 {step1Done && doneBadge}
                                    </div>
                                    <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>
                                        기공소 PC에 설치하면 CAD 작업 내역을 자동으로 수집합니다. (3분)
                                    </div>
                                </div>
                                {!step1Done && (
                                    <button onClick={() => navigate('/download-agent')}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                                        <HardDriveDownload size={15} /> 에이전트 설치
                                    </button>
                                )}
                            </div>
                            {/* ② CAD 폴더 연결 + 스캔 */}
                            <div style={{ ...rowStyle, borderBottom: '1px solid #f1f5f9' }}>
                                {stepCircle(step2Done, 2)}
                                <div style={{ flex: 1, minWidth: 220 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        {'CAD 작업 폴더 연결 후 [스캔하기]'} {step2Done && doneBadge}
                                    </div>
                                    <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>
                                        {'에이전트에서 [+ CAD 작업 폴더 연결]로 폴더를 등록하고 [스캔하기]를 누르면 지난 작업까지 한 번에 수집됩니다.'}
                                    </div>
                                </div>
                                {step2Done && (
                                    <button onClick={() => setCurrentView('inbox')}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                                        <FolderSearch size={15} /> 수집함 확인
                                        {inboxPendingCount > 0 && (
                                            <span style={{ background: '#fef3c7', color: '#92400e', fontWeight: 800, fontSize: 11, padding: '1px 7px', borderRadius: 10 }}>{inboxPendingCount}</span>
                                        )}
                                    </button>
                                )}
                            </div>
                            {/* ③ 월말 명세서 자동 완성 */}
                            <div style={rowStyle}>
                                {stepCircle(false, 3)}
                                <div style={{ flex: 1, minWidth: 220 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <CalendarCheck size={15} style={{ color: '#4338ca' }} /> 월말, 여기서 치과를 골라 명세서 자동 완성
                                    </div>
                                    <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>
                                        {'[거래명세서 작성]에서 치과를 고르면 그 달 수집분이 자동으로 채워집니다. '}
                                        <button onClick={() => navigate('/clinic-invites')}
                                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4f46e5', fontWeight: 700, fontSize: 12.5, textDecoration: 'underline' }}>
                                            거래 치과 초대
                                        </button>
                                        {'로 치과가 웹에서 직접 의뢰서를 보내게 할 수도 있습니다.'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* 월 마감 현황 보드 (기공소) — 숨김: '거래명세서 작성'과 중복되어 혼선 (2026-07) */}
            {false && isLabUser && boardRows.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
                            {getPeriod().label} 마감 현황
                            {boardLoading && <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>불러오는 중…</span>}
                        </div>
                        <button
                            onClick={createDraftsForAll}
                            disabled={batchCreating}
                            title="기간 내 의뢰서가 있고 명세서 미작성인 거래처 전체에 임시저장을 생성합니다 (발송되지 않음)"
                            style={{
                                padding: '7px 14px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, cursor: batchCreating ? 'wait' : 'pointer',
                                border: '1px solid #c7d2fe', background: batchCreating ? '#f1f5f9' : '#eef2ff', color: batchCreating ? '#94a3b8' : '#4338ca',
                            }}
                        >
                            {batchCreating ? '생성 중…' : '전체 임시저장 일괄 생성'}
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                            <thead><tr style={{ color: '#64748b', fontSize: 12, background: '#f8fafc' }}>
                                <th style={{ padding: '9px 12px', textAlign: 'left' }}>거래처</th>
                                <th style={{ padding: '9px 12px', textAlign: 'left' }}>이번 달 의뢰서</th>
                                <th style={{ padding: '9px 12px', textAlign: 'left' }}>자동 수집 대기</th>
                                <th style={{ padding: '9px 12px', textAlign: 'left' }}>명세서</th>
                                <th style={{ padding: '9px 12px', textAlign: 'left' }}>수수료</th>
                                <th style={{ padding: '9px 12px' }}></th>
                            </tr></thead>
                            <tbody>
                                {boardRows.map(r => (
                                    <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 12px', fontWeight: 700 }}>{r.name}</td>
                                        <td style={{ padding: '10px 12px' }}>{r.orders}건</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {r.parsedPending > 0
                                                ? <span style={{ background: '#fef3c7', color: '#92400e', fontWeight: 800, fontSize: 12, padding: '3px 10px', borderRadius: 12 }}>{r.parsedPending}건 확인 필요</span>
                                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {r.settled ? <span style={{ color: '#15803d', fontWeight: 700 }}>정산 완료</span>
                                                : r.issued ? <span style={{ color: '#b45309', fontWeight: 700 }}>발행됨</span>
                                                : r.draftId ? <span style={{ color: '#4338ca', fontWeight: 700 }}>작성 중</span>
                                                : <span style={{ color: '#dc2626', fontWeight: 700 }}>미작성</span>}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {r.matched
                                                ? <span style={{ background: '#eef2ff', color: '#4338ca', fontWeight: 700, fontSize: 11.5, padding: '3px 9px', borderRadius: 6 }}>덴클 매칭 4.5%</span>
                                                : <span style={{ fontSize: 12, color: '#94a3b8' }}>자체 거래처 · 없음</span>}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {!r.id.startsWith('email_') && (
                                                <button
                                                    onClick={() => requestCms(r)}
                                                    title="치과에 CMS 자동출금 동의를 요청합니다 — 동의 시 기공소가 정한 날짜에 자동 수금됩니다"
                                                    style={{ padding: '7px 12px', fontSize: 12, fontWeight: 700, border: '1px solid #bbf7d0', borderRadius: 8, cursor: 'pointer', background: '#f0fdf4', color: '#15803d', marginRight: 6 }}>
                                                    CMS 수금
                                                </button>
                                            )}
                                            <button
                                                onClick={() => { setEditDraftId(r.draftId); setCreateClinicId(r.id); setCurrentView('create'); }}
                                                style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer', background: r.issued ? '#f1f5f9' : '#6366f1', color: r.issued ? '#475569' : '#fff' }}>
                                                {r.draftId ? '이어서 작성' : r.issued ? '추가 작성' : '명세서 작성'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* 필터 */}
            <div style={{
                display: 'flex', gap: '12px', marginBottom: '20px',
                flexWrap: 'wrap', alignItems: 'center',
            }}>
                <select
                    value={selectedPartner}
                    onChange={(e) => setSelectedPartner(e.target.value)}
                    style={{
                        padding: '8px 12px', borderRadius: '8px',
                        border: '1px solid #e2e8f0', fontSize: '14px',
                    }}
                >
                    <option value="all">전체 거래처</option>
                    {partners.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>

                {/* 상태 칩 필터 (2026-08-26) — 클라이언트 필터. 폰: 줄바꿈 허용 (2026-10-24 재점검 P2-15) */}
                <div style={{ display: 'flex', flexWrap: 'wrap', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    {[['all', '전체'], ['draft', '작성 중'], ['issued', '발행됨'], ['disputed', '수정 요청 중'], ['settled', '정산 완료']].map(([v, lb]) => (
                        <button
                            key={v}
                            onClick={() => setStatusChip(v)}
                            style={{
                                padding: '8px 12px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer',
                                background: statusChip === v ? '#6366f1' : '#fff',
                                color: statusChip === v ? '#fff' : '#64748b',
                            }}
                        >
                            {lb}
                        </button>
                    ))}
                </div>

                {periodMode === 'month' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                            onClick={() => {
                                if (selectedMonth === 1) {
                                    setSelectedYear(selectedYear - 1);
                                    setSelectedMonth(12);
                                } else {
                                    setSelectedMonth(selectedMonth - 1);
                                }
                            }}
                            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer' }}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span style={{ fontWeight: '600', fontSize: '15px', minWidth: '100px', textAlign: 'center' }}>
                            {selectedYear}년 {selectedMonth}월
                        </span>
                        <button
                            onClick={() => {
                                if (selectedMonth === 12) {
                                    setSelectedYear(selectedYear + 1);
                                    setSelectedMonth(1);
                                } else {
                                    setSelectedMonth(selectedMonth + 1);
                                }
                            }}
                            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer' }}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                            type="date"
                            value={rangeStart}
                            max={rangeEnd || undefined}
                            onChange={(e) => setRangeStart(e.target.value)}
                            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#334155' }}
                        />
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>~</span>
                        <input
                            type="date"
                            value={rangeEnd}
                            min={rangeStart || undefined}
                            onChange={(e) => setRangeEnd(e.target.value)}
                            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#334155' }}
                        />
                    </div>
                )}

                {/* 더 보기 ▾ — 구분·월별/기간·날짜 기준을 접어 둠 (2026-10-23 §5-1). 기본값과 다르면 '설정됨' 표시 */}
                <button
                    onClick={() => setFilterMoreOpen(o => !o)}
                    aria-expanded={filterMoreOpen}
                    style={{
                        padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                        border: '1px solid #e2e8f0', borderRadius: '8px',
                        background: filterMoreOpen ? '#f1f5f9' : '#fff', color: '#475569',
                    }}
                >
                    {filterMoreOpen ? '더 보기 ▴' : '더 보기 ▾'}
                    {!filterMoreOpen && (partyChip !== 'all' || periodMode !== 'month' || dateBasis !== 'createdAt') && (
                        <span style={{ marginLeft: 6, fontSize: '11px', fontWeight: '700', color: '#6366f1' }}>{'설정됨'}</span>
                    )}
                </button>

                {/* 조회하기 — 현재 필터 기준으로 서버에서 최신 데이터 재조회 */}
                <button
                    onClick={() => { fetchPartners(); fetchStatements(); }}
                    disabled={loading}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '8px 16px', fontSize: '13.5px', fontWeight: '700',
                        border: 'none', borderRadius: '8px', cursor: loading ? 'default' : 'pointer',
                        background: '#6366f1', color: '#fff', opacity: loading ? 0.6 : 1,
                    }}
                >
                    <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
                    {loading ? '조회 중…' : '조회하기'}
                </button>
            </div>

            {/* 더 보기 펼침 — 기존 토글 그대로(동작 무변경), 누르면 바로 반영(useEffect) */}
            {filterMoreOpen && (
                <div style={{
                    display: 'flex', gap: '12px', marginTop: '-8px', marginBottom: '20px',
                    flexWrap: 'wrap', alignItems: 'center',
                }}>
                {/* [전체/치과/외주] (외주 의뢰서 확장 10-13) — 외주 명세서가 있을 때만. 받은 외주 명세서는 「매입」 */}
                {statements.some(st => OUTSOURCE_KINDS.includes(st.kind)) && (
                    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                        {[['all', '전체'], ['clinic', '치과'], ['outsource', '외주']].map(([v, lb]) => (
                            <button key={v} onClick={() => setPartyChip(v)}
                                style={{ padding: '8px 12px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer', background: partyChip === v ? '#6366f1' : '#fff', color: partyChip === v ? '#fff' : '#64748b' }}>
                                {lb}
                            </button>
                        ))}
                    </div>
                )}

                {/* 월별 / 기간 선택 토글 */}
                <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    {[['month', '월별'], ['range', '기간 선택']].map(([mode, label]) => (
                        <button
                            key={mode}
                            onClick={() => setPeriodMode(mode)}
                            style={{
                                padding: '8px 14px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer',
                                background: periodMode === mode ? '#6366f1' : '#fff',
                                color: periodMode === mode ? '#fff' : '#64748b',
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* 작성일 / 납기일 기준 토글 — 글자만 '납기일'(2026-10-23 §5-1). 값은 그대로 'dueDate' */}
                <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}
                    title="납기일 기준: 의뢰서는 납기일, 명세서는 기간 종료일로 집계합니다 (납기일 없는 의뢰서는 제외)">
                    {[['createdAt', '작성일'], ['dueDate', '납기일']].map(([basis, label]) => (
                        <button
                            key={basis}
                            onClick={() => setDateBasis(basis)}
                            style={{
                                padding: '8px 14px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer',
                                background: dateBasis === basis ? '#6366f1' : '#fff',
                                color: dateBasis === basis ? '#fff' : '#64748b',
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* 수집 원본 보기 (구 [수집함]) — 배지 없음 */}
                {isLabUser && !readOnly && (
                    <button
                        onClick={() => setCurrentView('inbox')}
                        title="에이전트가 CAD 파일에서 수집한 원본 목록을 봅니다"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: '#fff', color: '#475569',
                            border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        <Inbox size={15} /> {'수집 원본 보기'}
                    </button>
                )}

                </div>
            )}

            {/* 작성 중(draft) 고정 섹션 (2026-08-26) — 기간 무관 항상 표시 (월 필터에 안 걸림) */}
            {!loading && statusChip !== 'draft' && (() => {
                const drafts = statements.filter(s =>
                    s.direction === 'sent' && s.status === 'draft' &&
                    (selectedPartner === 'all' || s.toUserId === selectedPartner));
                if (drafts.length === 0) return null;
                return (
                    <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                        <div style={{ fontWeight: 800, fontSize: 14, color: '#3730a3', marginBottom: 8 }}>
                            아직 안 보낸 명세서 {drafts.length}장
                            <span style={{ fontWeight: 500, fontSize: 12, color: '#6366f1', marginLeft: 8 }}>· 기간과 상관없이 항상 보입니다</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {drafts.map(s => (
                                <div key={s.id}
                                    onClick={() => { if (readOnly) { setSelectedStatementId(s.id); setCurrentView('detail'); return; } setEditDraftId(s.id); setCurrentView('create'); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}>
                                    <span style={{ fontWeight: 700, color: '#0f172a', minWidth: 120 }}>{s.toUserName || '-'}</span>
                                    <span style={{ color: '#6366f1', fontWeight: 600 }}>{s.statementNumber || '-'}</span>
                                    <span style={{ color: '#64748b' }}>{formatDate(s.startDate)} ~ {formatDate(s.endDate)}</span>
                                    <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{formatCurrency(s.totalAmount)}원</span>
                                    {!readOnly && (<>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setEditDraftId(s.id); setCurrentView('create'); }}
                                        style={{ padding: '5px 12px', fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', cursor: 'pointer' }}>
                                        이어서 작성
                                    </button>
                                    <button
                                        onClick={(e) => handleDeleteDraft(e, s)}
                                        disabled={deletingId === s.id}
                                        title="작성 중 명세서 삭제"
                                        style={{ display: 'inline-flex', padding: 5, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: deletingId === s.id ? 'wait' : 'pointer' }}>
                                        <Trash2 size={13} />
                                    </button>
                                    </>)}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* 월 합계 요약 */}
            {!loading && filteredStatements.length > 0 && (() => {
                // 임시저장(draft)은 아직 발행 전 — 합계에서 제외
                // (P1-1, 2026-08-26) 발행처가 삭제 표기한(deletedBySender) 수신본도 합계에서 제외 — 과입금 방지
                // (2026-10-23 P1) 합계는 한 기준으로만 — 목록은 작성일·기간 두 달에 걸쳐 보일 수 있지만, 합계는 세무 자료 엑셀과 같은
                //   기준(기간 종료일 → 발행일 → 작성일의 월이 선택 기간 안, utils/statementView statementMonthKeyDate)으로 센다. 마감일 기준 보기는 현행 그대로
                const { start: sumStart, end: sumEnd } = getPeriod();
                const basisStatements = dateBasis === 'dueDate' ? filteredStatements : filteredStatements.filter(s => {
                    if (s.status === 'draft') return true;
                    const d = statementMonthKeyDate(s, toJsDateX);
                    return !!d && d >= sumStart && d < sumEnd;
                });
                const deletedCount = basisStatements.filter(s => s.status !== 'draft' && s.deletedBySender).length;
                const issuedStatements = basisStatements.filter(s => s.status !== 'draft' && !s.deletedBySender);
                const sentStatements = issuedStatements.filter(s => s.direction === 'sent');
                const receivedStatements = issuedStatements.filter(s => s.direction === 'received');
                const sentTotal = sentStatements.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
                const receivedTotal = receivedStatements.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
                // (2026-10-13 대표 승인) 발행(매출)과 수신(매입)을 한 숫자로 더하지 않는다.
                //   둘 다 있으면 큰 카드는 발행 합계, 수신은 옆 카드(매입)로만 표시. 한쪽만 있으면 그 합계 그대로.
                const mixedDirections = sentStatements.length > 0 && receivedStatements.length > 0;
                const grandTotal = mixedDirections
                    ? sentTotal
                    : issuedStatements.reduce((sum, s) => sum + (s.totalAmount || 0), 0);

                return (
                    <div style={{
                        display: 'flex', gap: '12px', marginBottom: '20px',
                        flexWrap: 'wrap',
                    }}>
                        <div style={{
                            flex: '1', minWidth: '200px',
                            background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                            borderRadius: '12px', padding: '16px',
                            color: 'white',
                        }}>
                            <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '4px' }}>
                                                {getPeriod().label} {mixedDirections ? '발행 합계' : '합계'}
                            </div>
                            <div style={{ fontSize: '22px', fontWeight: '700' }}>
                                {formatCurrency(grandTotal)}원
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.75, marginTop: '4px' }}>
                                총 {mixedDirections ? sentStatements.length : basisStatements.length}건
                                {mixedDirections && <span style={{ marginLeft: 6 }}>{'· 수신(매입) 미포함'}</span>}
                                {deletedCount > 0 && <span style={{ marginLeft: 6 }}>· 삭제 표기 {deletedCount}건 제외</span>}
                            </div>
                        </div>

                        <div style={{
                            flex: '1', minWidth: '200px',
                            background: 'white', borderRadius: '12px',
                            border: '1px solid #e2e8f0', padding: '16px',
                        }}>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                                발행 합계
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#4338ca' }}>
                                {formatCurrency(sentTotal)}원
                            </div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                                {sentStatements.length}건
                            </div>
                        </div>

                        <div style={{
                            flex: '1', minWidth: '200px',
                            background: 'white', borderRadius: '12px',
                            border: '1px solid #e2e8f0', padding: '16px',
                        }}>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                                {'수신 합계 (매입)'}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#c2410c' }}>
                                {formatCurrency(receivedTotal)}원
                            </div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                                {receivedStatements.length}건
                            </div>
                        </div>
                        {dateBasis !== 'dueDate' && (
                            <div style={{ flexBasis: '100%', fontSize: '11.5px', color: '#94a3b8', marginTop: '-4px' }}>
                                {'합계는 기간 종료일 기준(엑셀과 같음)'}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* 일부 쿼리 실패 경고 (2026-10-23) — 조용한 실패 금지 */}
            {!loading && loadError === 'partial' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>{'일부 명세서를 불러오지 못했습니다 — 목록이 빠져 있을 수 있습니다'}</span>
                    <button onClick={fetchStatements}
                        style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, border: '1px solid #fde68a', borderRadius: 6, background: '#fff', color: '#92400e', cursor: 'pointer' }}>
                        {'다시 시도'}
                    </button>
                </div>
            )}

            {/* 테이블 목록 */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                    로딩 중...
                </div>
            ) : loadError === 'full' ? (
                // 불러오기 실패 — 빈 목록 안내 대신 오류 + 다시 시도 (2026-10-23)
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#b91c1c', fontSize: 14 }}>
                    {'명세서를 불러오지 못했습니다.'}
                    <button onClick={fetchStatements}
                        style={{ marginLeft: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, border: '1px solid #fecaca', borderRadius: 8, background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>
                        {'다시 시도'}
                    </button>
                </div>
            ) : filteredStatements.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                    <FileText size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                    {isClinicUser && statements.length === 0 ? (
                        <p style={{ lineHeight: 1.7 }}>
                            {'거래 기공소가 발행한 명세서(계산서)가 이곳에 도착합니다.'}<br />
                            {'아직 받은 문서가 없습니다.'}
                        </p>
                    ) : (
                        <p>해당 기간의 거래명세서가 없습니다.</p>
                    )}
                </div>
            ) : (
                // 모바일: 열 압착 대신 가로 스크롤 (overflowX auto + 테이블 minWidth)
                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
                    {/* 2026-10-07 대표 지시(비율 복원): 열 비율은 원래대로, 상태 6%→8%만. 미리보기는 아이콘 버튼으로 배지 옆 */}
                    <table style={{ width: '100%', minWidth: showPayCol ? '1040px' : '960px', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <colgroup>
                            <col style={{ width: '7%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '12%' }} />
                            {/* 입금 열은 합계 뒤·상태 앞 — 돈 열끼리 붙여 읽기 쉽게 (2026-10-24 재점검 P2-16) */}
                            {showPayCol && <col style={{ width: '7%' }} />}
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '8%' }} />
                        </colgroup>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={thStyle}>구분</th>
                                <th style={thStyle}>명세서번호</th>
                                <th style={thStyle}>거래처</th>
                                <th style={thStyle}>거래 기간</th>
                                <th style={thStyle}>건수</th>
                                <th style={thStyle}>치아수</th>
                                <th style={thStyle}>합계 금액</th>
                                {showPayCol && <th style={{ ...thStyle, textAlign: 'center' }} title="기공소가 직접 기록한 입금 기준 — 먼저 보낸 명세서부터 입금된 것으로 봅니다">입금</th>}
                                <th style={thStyle}>작성일</th>
                                <th style={thStyle}>발행일</th>
                                <th style={thStyle}>상태</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStatements.map((s, index) => (
                                <tr
                                    key={s.id}
                                    onClick={() => {
                                        // 임시저장(내가 보낸 것)은 작성 화면에서 이어서 편집
                                        if (s.status === 'draft' && s.direction === 'sent' && !readOnly) {
                                            setEditDraftId(s.id);
                                            setCurrentView('create');
                                        } else {
                                            setSelectedStatementId(s.id);
                                            setCurrentView('detail');
                                        }
                                    }}
                                    style={{
                                        borderBottom: '1px solid #f1f5f9',
                                        background: index % 2 === 1 ? '#f8fafc' : '#fff',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 1 ? '#f8fafc' : '#fff'}
                                >
                                    {/* 구분 (발행/수신) */}
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                            background: s.direction === 'sent' ? '#eef2ff' : '#fff7ed',
                                            color: s.direction === 'sent' ? '#4338ca' : '#c2410c',
                                        }}>
                                            {s.direction === 'sent' ? '발행' : '수신'}
                                        </span>
                                    </td>
                                    {/* 명세서번호 */}
                                    <td style={{ ...tdStyle, color: '#6366f1', fontWeight: '600' }}>
                                        {s.statementNumber || '-'}
                                    </td>
                                    {/* 거래처 */}
                                    <td style={{ ...tdStyle, fontWeight: '600', color: '#0f172a' }}>
                                        {s.direction === 'sent' ? s.toUserName : s.fromUserName}
                                    </td>
                                    {/* 거래 기간 */}
                                    <td style={{ ...tdStyle, color: '#64748b' }}>
                                        {formatDate(s.startDate)} ~ {formatDate(s.endDate)}
                                    </td>
                                    {/* 건수 */}
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        {(s.items || []).length}건
                                    </td>
                                    {/* 치아수 */}
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        {s.totalTeeth || 0}개
                                    </td>
                                    {/* 합계 금액 */}
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                                        {formatCurrency(s.totalAmount)}원
                                    </td>
                                    {/* 입금 (2026-10-23 1단계) — 다 받음/일부/안 받음. 정산(치과 확인)과 별개. 합계 바로 뒤 (P2-16) */}
                                    {showPayCol && (() => {
                                        const info = (s.direction === 'sent' && isLedgerStatement(s))
                                            ? paymentStateInfo(statementPaymentDetail(s, recvByPartner[partnerKeyOfStatement(s)])) : null;
                                        return (
                                            <td style={{ ...tdStyle, textAlign: 'center' }}
                                                title={info?.state === 'partial' && info.remaining != null ? `${formatCurrency(info.remaining)}원 남음` : undefined}>
                                                {info ? (
                                                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, ...PAYMENT_STATE_STYLE[info.state] }}>{info.label}</span>
                                                ) : <span style={{ color: '#cbd5e1' }}>-</span>}
                                            </td>
                                        );
                                    })()}
                                    {/* 작성일 (createdAt) */}
                                    <td style={{ ...tdStyle, color: '#64748b', textAlign: 'center' }}>
                                        {formatDate(s.createdAt)}
                                    </td>
                                    {/* 발행일 (publishedAt, 없으면 옛 발행본은 작성일) — 정산일(settledAt)로 채우지 않음 (2026-10-23 §5-1) */}
                                    <td style={{ ...tdStyle, color: '#64748b', textAlign: 'center' }}>
                                        {s.publishedAt ? formatDate(s.publishedAt)
                                            : s.status === 'confirmed' && s.createdAt ? formatDate(s.createdAt)
                                            : '-'}
                                    </td>
                                    {/* 상태 */}
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        {s.direction === 'received' && s.deletedBySender ? (
                                            // 발행처가 삭제한 수신본 (2026-08-26) — 정산 등 액션 대신 배지 + [목록에서 제거]
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                                                    {s.correctedTo ? '정정됨' : '발행처에서 삭제함'}
                                                </span>
                                                {/* 정산 완료본은 규칙상 받는 쪽 삭제 불가 — '정정됨' 배지로 남김 (2026-10-23 검토 회의) */}
                                                {!readOnly && s.settlementStatus !== 'settled' && <button
                                                    onClick={(e) => removeDeletedReceived(e, s)}
                                                    disabled={deletingId === s.id}
                                                    title="내 목록에서 이 명세서를 제거합니다"
                                                    style={{
                                                        padding: '4px 10px', fontSize: 11.5, fontWeight: 700, borderRadius: 6,
                                                        border: '1px solid #fecaca', background: '#fff', color: '#dc2626',
                                                        cursor: deletingId === s.id ? 'wait' : 'pointer', opacity: deletingId === s.id ? 0.5 : 1,
                                                    }}>
                                                    목록에서 제거
                                                </button>}
                                                {/* 삭제본도 보기·인쇄는 허용 (이미 받은 문서) — 모달에 삭제 안내 배너 */}
                                                {previewButton(s)}
                                            </div>
                                        ) : (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            {getStatusBadge(s)}
                                            {/* 미리보기 = 읽기 전용 모달. 발행본만, 치과·기공소 양쪽 (D3) */}
                                            {s.status !== 'draft' && previewButton(s)}
                                            {s.direction === 'sent' && !readOnly && (
                                                // 발행본은 목록에서 바로 삭제 불가 — 버튼을 숨기지 않고 눌렀을 때 이유를 안내한다 (2026-08-08)
                                                <button
                                                    onClick={(e) => handleDeleteDraft(e, s)}
                                                    disabled={deletingId === s.id}
                                                    title={s.status === 'draft'
                                                        ? '작성 중 명세서 삭제'
                                                        : '발행된 명세서는 명세서를 열어서 삭제'}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 5, borderRadius: 6,
                                                        border: s.status === 'draft' ? '1px solid #fecaca' : '1px solid #e2e8f0',
                                                        background: s.status === 'draft' ? '#fef2f2' : '#f8fafc',
                                                        color: s.status === 'draft' ? '#dc2626' : '#94a3b8',
                                                        cursor: deletingId === s.id ? 'wait' : 'pointer',
                                                        opacity: deletingId === s.id ? 0.5 : 1,
                                                    }}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 미리보기 모달 (2026-10-06) — 읽기 전용, 정산 처리는 [정산 처리하러 가기]로 기존 상세 진입 */}
            {previewStatement && (
                <StatementPreviewModal
                    statement={previewStatement}
                    partnerName={previewStatement.direction === 'sent' ? previewStatement.toUserName : previewStatement.fromUserName}
                    statusBadge={getStatusBadge(previewStatement)}
                    position={{ index: previewIdx, total: previewables.length }}
                    onPrev={previewIdx > 0 ? () => setPreviewId(previewables[previewIdx - 1].id) : null}
                    onNext={previewIdx < previewables.length - 1 ? () => setPreviewId(previewables[previewIdx + 1].id) : null}
                    canGoDetail={canGoDetailFromPreview(previewStatement)}
                    onGoDetail={() => {
                        const id = previewStatement.id;
                        setPreviewId(null);
                        setSelectedStatementId(id);
                        setCurrentView('detail');
                    }}
                    onClose={() => setPreviewId(null)}
                />
            )}

            {/* 💰 예상 기공료 모달 (2026-10-07) — 서버 응답 그대로. 정산 버튼·저장 없음 */}
            {expData && (
                <ExpectedStatementModal data={expData} viewer="clinic" onClose={() => setExpData(null)} />
            )}
        </div>
    );
}

export default TransactionStatementList;
